import {Dosage, DosageTime, DosageTimeWithTrigger} from "@/dosage/interfaces/Dosage";
import {pluralize} from "@/core/filters/Pluralize";
import {ProductDose} from "@/product/interfaces/ProductDose";

const DosageService = () => {
    return {
        /**
         * Type guard to check if a dosage time has a time trigger defined.
         */
        isDosageTimeWithTrigger(dosage: DosageTime): dosage is DosageTimeWithTrigger {
            return !!(dosage as DosageTimeWithTrigger).dosage_time_trigger;
        },

        /**
         * Get a human readable version of the dosage.
         *
         * e.g. "1 dose le matin, 2 doses à 14:00, 1 dose si douleur prise orale (7j/7)"
         */
        humanizeDosage(dosage: Pick<Dosage, 'times' | 'dose' | 'frequency' | 'instruction'>): string {
            const parts: string[] = [];
            const form = dosage.dose;

            if (dosage.times && dosage.times.length) {
                parts.push(this.humanizeTimes(dosage.times, form));
            }

            if (dosage.instruction?.name) {
                parts.push(dosage.instruction.name.toLowerCase());
            }

            if (dosage.frequency?.name) {
                parts.push(`(${dosage.frequency.name.toLowerCase()})`);
            }

            return parts.join(' ');
        },

        /**
         * Humanize the dosage times.
         *
         * e.g. "1 dose le matin, 2 doses à 14:00, 1 dose si douleur"
         *
         * If a form is provided, use the form name instead of "dose".
         */
        humanizeTimes(times: DosageTime[], form?: ProductDose): string {
            const timeDescriptions = times.map(time => {
                const doseAmount = time.dose;
                const unit = !form
                    ? pluralize('dose', doseAmount)
                    : doseAmount > 1 ? form.plural_name : form.singular_name;

                const doseText = `${doseAmount} ${unit}`;

                if (this.isDosageTimeWithTrigger(time)) {
                    return `${doseText} ${time.dosage_time_trigger.name.toLowerCase()}`;
                } else {
                    return `${doseText} à ${time.time}`;
                }
            });

            return timeDescriptions.join(', ');
        }
    };
};

export default DosageService();
